import { useState, useEffect } from "react";
import api, { errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/CrudPage";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const MODULES = [
  ["dashboard", "Dashboard"], ["sales", "Satış"], ["sales_history", "Satış Geçmişi"],
  ["categories", "Ana Kategoriler"], ["subcategories", "Alt Kategoriler"],
  ["products", "Ürünler"], ["stock_movements", "Stok Hareketleri"], ["equipment", "Envanter"],
  ["agenda", "Ajanda"], ["calendar", "Takvim"], ["appointments", "Randevular"],
  ["customers", "Müşteriler"], ["suppliers", "Tedarikçiler"],
  ["income", "Gelir"], ["expense", "Gider"], ["cash", "Kasa"],
  ["reports", "Raporlar"], ["users", "Kullanıcılar"],
  ["permissions", "Yetkilendirme"], ["settings", "Ayarlar"],
];

const ACTIONS = [
  ["view", "Görüntüle"], ["create", "Ekle"], ["edit", "Düzenle"],
  ["delete", "Sil"], ["export", "Export"],
];

export default function Permissions() {
  const { hasPerm } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users", { params: { limit: 100 } }).then((r) => {
      const staff = r.data.items.filter((u) => u.role !== "super_admin");
      setUsers(staff);
    });
  }, []);

  const selectUser = (id) => {
    setSelectedId(id);
    const u = users.find((x) => x.id === id);
    setPerms(u?.permissions || {});
  };

  const toggle = (module, action) =>
    setPerms((p) => ({ ...p, [module]: { ...p[module], [action]: !p[module]?.[action] } }));

  const toggleRow = (module) => {
    const all = ACTIONS.every(([a]) => perms[module]?.[a]);
    setPerms((p) => ({ ...p, [module]: Object.fromEntries(ACTIONS.map(([a]) => [a, !all])) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${selectedId}/permissions`, { permissions: perms });
      toast.success("Yetkiler kaydedildi");
      setUsers((us) => us.map((u) => (u.id === selectedId ? { ...u, permissions: perms } : u)));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="permissions-page">
      <PageHeader title="Yetkilendirme" subtitle="Kullanıcı bazlı modül yetkilerini yönetin (RBAC)">
        {selectedId && hasPerm("permissions", "edit") && (
          <Button data-testid="permissions-save-button" disabled={saving} onClick={save}
            className="rounded-none bg-[#D4AF37] font-semibold text-[#0B1F3A] hover:bg-[#c6a230]">
            {saving ? "Kaydediliyor..." : "Yetkileri Kaydet"}
          </Button>
        )}
      </PageHeader>

      <div className="mb-6 max-w-sm">
        <Select value={selectedId} onValueChange={selectUser}>
          <SelectTrigger data-testid="permissions-user-select" className="rounded-none">
            <SelectValue placeholder="Kullanıcı seçin" />
          </SelectTrigger>
          <SelectContent>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
          </SelectContent>
        </Select>
        {users.length === 0 && <p className="mt-2 text-sm text-gray-400">Henüz personel kullanıcısı yok. Kullanıcılar sayfasından ekleyin.</p>}
      </div>

      {selectedId && (
        <div className="overflow-x-auto border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Modül</th>
                {ACTIONS.map(([a, l]) => (
                  <th key={a} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">{l}</th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Tümü</th>
              </tr>
            </thead>
            <tbody>
              {MODULES.map(([m, label]) => (
                <tr key={m} className="border-b border-gray-100 transition-colors duration-200 hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium">{label}</td>
                  {ACTIONS.map(([a]) => (
                    <td key={a} className="px-4 py-3 text-center">
                      <Checkbox data-testid={`perm-${m}-${a}`} checked={Boolean(perms[m]?.[a])} onCheckedChange={() => toggle(m, a)} />
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`perm-${m}-all`} onClick={() => toggleRow(m)}
                      className="text-xs font-semibold text-[#D4AF37] hover:underline">Tümü</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
